<script setup>
    // Composables
    const { $bus } = useNuxtApp();

    import { isPreviewing } from '@builder.io/sdk-vue';
    import { runHubspotCompliance } from '@/composables/hubspot/compliance';
    import { initializeWindowEvents } from '@/composables/helpers/layouts/window';
    import { updateGlobals } from '@/composables/data/updateGlobals';

    // Keeps the sealed session cookie in step with the server on tab focus and
    // across sibling tabs. Mounted HERE, at the root, on purpose: the My
    // Schedule tab watcher in agenda.vue was the only thing re-reading the
    // server, so a tab sitting on the sessions list or a session detail page
    // stayed stale indefinitely — a hard reload included, since SSR reads the
    // same cookie. It no-ops for signed-out visitors.
    useScheduleFreshness();

    const route = useRoute();
    const locked = ref(false);
    const showSpeakerModal = ref(false);
    const showSessionDrawer = ref(false);
    const showReservationsModal = ref(false);
    const showLeaveModal = ref(false);
    const showSearchModal = ref(false);
    const showBraindate = ref(false);
    const showFavoritedModal = ref(false);
    const showRegistrationModal = ref(false);
    const lastFavorited = ref(false);

    // Fetch Global Data
    const { data: global } = await useAsyncData('all-global-data', () => $fetch('/api/global-data'));

    // Release timestamps for the My Schedule / Save a Seat features. Stored in a
    // useState (persisted SSR→client) so any session card can resolve button
    // visibility client-side — see useScheduleControls.
    useState('scheduleControls', () => ({ enableMyScheduleAfter: null, enableSeatReservationsAfter: null, enableSeatReservationsGeneralAfter: null, alwaysEnableSeatReservationsFor: [] })).value = {
        enableMyScheduleAfter: global.value?.data?.enableMyScheduleAfter ?? null,
        // VIP wave, then the general wave. See useScheduleControls.
        enableSeatReservationsAfter: global.value?.data?.enableSeatReservationsAfter ?? null,
        enableSeatReservationsGeneralAfter: global.value?.data?.enableSeatReservationsGeneralAfter ?? null,
        // Addresses granted the VIP wave regardless of ticket tier.
        alwaysEnableSeatReservationsFor: global.value?.data?.alwaysEnableSeatReservationsFor ?? [],
    };

    // The Save-a-Seat return-visit prompt, fired on LOG IN (Figma flow "Seat
    // Reservations Open"). It lives here rather than in the agenda because
    // logging in does not move the visitor: /login is a modal the global
    // middleware rewrites out of the path, so the login completes over whatever
    // page they were already on. The agenda's own trigger stays — it is the only
    // one that can reach someone who was ALREADY logged in when reservations
    // opened, since they never fire an auth:login at all. useSeatPrompt owns a
    // once-ever gate, so the two can't both land.
    if (import.meta.client) {
        const seatPrompt = useSeatPrompt();
        $bus.on('auth:login', async ({ action } = {}) => {
            // A login carrying an action was a detour: the visitor clicked
            // something, got sent to log in, and is being handed back to it right
            // now. Interrupting that hand-off with a modal buries the thing they
            // actually asked for, so let the prompt wait for their next visit.
            if (action) return;

            // Server-resolved: off the agenda there is no catalog here, and no
            // reliable year to fetch one with. See the endpoint's own note.
            const pending = await $fetch('/api/v1/schedule/pending-seats')
                .then((r) => r?.sessions || [])
                .catch(() => []);
            if (!pending.length) return;

            await seatPrompt.maybeShowOnReturn(pending, async () => {
                // Same destination the agenda's trigger uses (annotation
                // 4669:65271): My Schedule, Detail view, filtered to the sessions
                // still needing a seat.
                await navigateTo({
                    path: '/agenda',
                    query: { tab: 'myschedule', view: 'list', seats_needed: 1 },
                });
            });
        });

        // Conflicts within an already-saved schedule, raised on login. AWAITED
        // after the seat prompt above, and gated inside on that prompt having
        // been dismissed — stacking the two would bury one of them.
        const conflicts = useScheduleConflicts();
        $bus.on('auth:login', async ({ action } = {}) => {
            if (action) return;
            await conflicts.maybeShow(async () => {
                // Same destination the agenda's own trigger uses: My Schedule,
                // Detail view, narrowed to just the clashing sessions.
                await navigateTo({
                    path: '/agenda',
                    query: { tab: 'myschedule', view: 'calendar', conflicts: 1 },
                });
            });
        });
    }

    if (process.env.NODE_ENV === 'production') {
        useHead({
            script: [
                {
                    id: 'embedScript',
                    src: '//js.hsforms.net/forms/embed/v2.js',
                    type: 'text/javascript',
                    defer: true,
                },
            ],
        });
    }

    const loadCompliance = () => {
        if (process.env.NODE_ENV !== 'production') return;
        useHead({
            script: [
                {
                    id: 'hubspot-compliance',
                    src: 'https://www.hubspot.com/wt-assets/static-files/compliance/index.js',
                    defer: true,
                    type: 'text/javascript',
                    'data-external-hs-domain': true,
                    'data-gtm-id': 'GTM-T7GN22X',
                    'data-pid': '146726',
                    nonce: true,
                },
            ],
        });
        if (!isPreviewing(route.fullPath)) runHubspotCompliance();
    };

    await updateGlobals(unref(global));

    if (process.client) {
        initializeWindowEvents();
    }

    if (process.client) {
        $bus.on('intro-complete', loadCompliance);
    }

    watch(
        () => route.query,
        (newQuery) => {
            if (!process.client) return;
            setTimeout(() => {
                if (newQuery.drawer && /^\/speakers\/[\w-]+$/.test(newQuery.drawer)) showSpeakerModal.value = true;
                else showSpeakerModal.value = false;
                if (newQuery.drawer && /^\/sessions\/[\w-]+$/.test(newQuery.drawer)) showSessionDrawer.value = true;
                else showSessionDrawer.value = false;
                if (newQuery.modal && /^\/braindate\/?$/.test(newQuery.modal)) showBraindate.value = true;
                else showBraindate.value = false;
                if (newQuery.modal && /^\/search\/?$/.test(newQuery.modal)) showSearchModal.value = true;
                else showSearchModal.value = false;
                if (newQuery.modal && /^\/reservations\/\w+$/.test(newQuery.modal)) showReservationsModal.value = true;
                else showReservationsModal.value = false;
                if (newQuery.modal && /^\/leave\/\w+$/.test(newQuery.modal)) showLeaveModal.value = true;
                else showLeaveModal.value = false;
                if (newQuery.modal && /^\/register-modal\/?$/.test(newQuery.modal)) showRegistrationModal.value = true;
                else showRegistrationModal.value = false;
                if (showSpeakerModal.value || showSessionDrawer.value || showReservationsModal.value || showSearchModal.value || showBraindate.value || showRegistrationModal.value) {
                    if (process.client) document.documentElement.classList.add('locked');
                } else {
                    if (process.client) document.documentElement.classList.remove('locked');
                }
            }, 0);
        },
        { immediate: true }
    );

    const speaker_id = computed(() => {
        if (!route.query.drawer) return null;
        const match = String(route.query.drawer).match(/^\/speakers\/([\w-]+)$/);
        return match ? match[1] : null;
    });

    const drawer_session_id = computed(() => {
        if (!route.query.drawer) return null;
        const match = String(route.query.drawer).match(/^\/sessions\/([\w-]+)$/);
        return match ? match[1] : null;
    });

    const session_id = computed(() => {
        if (!route.query.modal) return null;
        const match = String(route.query.modal).match(/^\/(reservations|leave)\/(.+)$/);
        return match && match.length > 1 ? match[2] : null;
    });

    $bus.on('drawer-open', () => {
        locked.value = true;
        if (process.client) {
            document.documentElement.classList.add('locked');
        }
    });

    $bus.on('drawer-close', () => {
        locked.value = false;
        if (process.client) {
            document.documentElement.classList.remove('locked');
        }
    });

    $bus.on('favorited', (session) => {
        if (session.reservations_required) {
            lastFavorited.value = session;
            showFavoritedModal.value = true;
        }
    });
</script>

<template>
    <UApp>
        <div id="app-wrapper" :class="{ locked: locked }">
            <FragmentsShardClipPaths />
            <NuxtLoadingIndicator />
            <NuxtRouteAnnouncer />
            <MessBlocker />
            <div class="root-wrapper">
                <div class="layout-wrapper" :class="{ legacy: route.meta.layout !== '2026' }">
                    <MessMetaActions />
                    <NuxtLayout>
                        <NuxtPage />
                    </NuxtLayout>
                    <FragmentsDialogsFavorited v-model:open="showFavoritedModal" :session="lastFavorited" />
                    <FragmentsDialogsSearch v-model:open="showSearchModal" />
                    <!-- v-if, matching the session drawer below. Without it this
                         drawer is mounted for the life of the tab and ONE UDrawer
                         instance is reused for every speaker, so its Reka dialog
                         state accumulates across open/close cycles until a child
                         re-renders without its DialogRoot ("Injection
                         Symbol(DialogRootContext) not found", thrown from the
                         route-query watcher above). Once that happens the overlay
                         is wedged and a concealed modal underneath — Find a
                         Session's recommendations — can never come back.
                         Rebuilding per speaker keeps each open on a fresh root. -->
                    <FragmentsDrawersSpeaker v-if="speaker_id" v-model:open="showSpeakerModal" :speaker_id="speaker_id" />
                    <FragmentsDrawersSession v-if="drawer_session_id" v-model:open="showSessionDrawer" :session_id="drawer_session_id" />
                    <FragmentsDialogsBraindate v-model:open="showBraindate" />
                    <FragmentsDialogsReservations v-model:open="showReservationsModal" :session_id="session_id" />
                    <FragmentsDialogsLeave v-model:open="showLeaveModal" :session_id="session_id" />
                    <FragmentsDialogsRegistration v-model:open="showRegistrationModal" />
                </div>
            </div>
        </div>
    </UApp>
</template>

<style lang="scss">
    @layer legacy {
        @import '@/assets/styles/main.scss';
    }

    #hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner p {
        border-bottom: none !important;
    }

    body #hs-banner-parent div#hs-eu-cookie-confirmation {
        position: fixed !important;
        z-index: 9999;
        left: unset !important;
        right: unset !important;
        inset: unset !important;
    }

    body #hs-banner-parent.hs-banner-parent-fixed div#hs-eu-cookie-confirmation .hs-eu-cookie-close-button button {
        color: transparent !important;
    }

    #hs-eu-cookie-confirmation #hs-eu-close-button svg {
        color: white !important;
    }

    #hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner {
        background: #213343 !important;
        box-sizing: content-box !important;
        margin: 0 auto !important;
        max-width: 1080px !important;
        padding: 0 2rem !important;
        gap: 0 !important;

        #hs-eu-cookie-settings-button {
            min-width: 8rem;
        }
    }

    #hs-banner-parent #hs-eu-cookie-confirmation {
        inset: 0 0 auto 50% !important;
        background: #213343 !important;
        padding: 8px !important;
        width: 100% !important;
        border-radius: 0 !important;
        max-width: unset !important;
    }

    #hs-banner-parent * {
        font: inherit;
        color: inherit;
        background: none;
        border: none;
        box-shadow: none;
        box-sizing: border-box;
        margin: 0;
        padding: 0;
        display: inline-block;
        line-height: 1.75em;
    }

    div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner p {
        letter-spacing: 0.2px;
    }

    #hs-eu-header-container {
        display: block !important;

        #hs-eu-close-button {
            position: absolute;
            top: 0;
            right: 10px;
            font-size: 32px;
            font-weight: 400;
            color: white;
            font-family: var(--lexend);
            line-height: 1.4rem;
        }
    }

    #hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area {
        margin-top: 2px !important;
    }

    div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner p {
        margin: 0 72px 12px;
        color: #fff !important;
        font-family: inherit;
        font-size: 12px !important;
        font-weight: 400 !important;
        line-height: inherit;
        text-align: left;
        text-shadow: none !important;
    }

    #hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner p {
        font-size: 0.9rem !important;
        font-weight: 300 !important;
        line-height: 1.2rem !important;
        color: #fff !important;
        font-family: 'Lexend Deca', 'LexendDeca', 'Helvetica Neue', helvetica, arial, sans-serif !important;
        margin: 0 0 0.5rem !important;
        padding: 0 !important;
        margin-right: 0 !important;
        text-align: left !important;
    }

    #hs-eu-cookie-settings-button {
        font-family: var(--lexend) !important;
        font-weight: 500 !important;
        color: #fff !important;
        text-decoration: none !important;

        &:hover {
            text-decoration: underline !important;
        }
    }

    #hs-modal-accept-all,
    #hs-modal-save-settings {
        text-align: center;
    }

    #hs-eu-cookie-confirmation #hs-eu-confirmation-button-group,
    #hs-eu-cookie-confirmation #hs-eu-opt-in-buttons {
        gap: 1rem !important;
    }

    #hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area button#hs-eu-confirmation-button,
    #hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area button#hs-eu-decline-button {
        background-color: #fff !important;
        border: 0 !important;
        border-radius: 0.1875rem !important;
        box-shadow: none !important;
        color: #2e475d !important;
        display: inline-block !important;
        line-height: 1.25 !important;
        text-shadow: none !important;
        transition:
            0.15s color ease-out,
            0.15s background-color ease-out,
            0.15s border-color ease-out !important;
        font-family: 'Lexend Deca', 'LexendDeca', 'Helvetica Neue', helvetica, arial, sans-serif !important;
        font-weight: 500 !important;
        padding: 0 0.5rem !important;
        height: 3em !important;
    }

    #hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area button {
        max-width: 10rem !important;
    }

    #hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area {
        justify-content: center !important;
        margin-bottom: 0.5rem !important;
        align-items: center !important;
        gap: 1rem !important;
    }

    #hs-banner-parent div#hs-eu-cookie-confirmation #hs-eu-cookie-confirmation-buttons-area {
        display: flex !important;
        margin-right: inherit !important;
        margin-top: 0 !important;
        position: relative !important;
        place-content: center !important;
        width: 100% !important;
    }

    #hs-eu-cookie-confirmation #hs-eu-cookie-confirmation-buttons-area {
        display: flex;
        flex-flow: row wrap;
        justify-content: flex-end;
        align-items: baseline;
    }

    #hs-banner-parent * {
        font: inherit;
        color: inherit;
        background: none;
        border: none;
        box-shadow: none;
        box-sizing: border-box;
        margin: 0;
        padding: 0;
        display: inline-block;
        line-height: 1.75em;
    }

    #hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner p {
        font-size: 12.8px !important;
        padding-bottom: 6px !important;
        border-bottom: 1px solid #516f90;
    }

    #hs-banner-parent
        div#hs-eu-cookie-confirmation
        div#hs-eu-cookie-confirmation-inner
        #hs-eu-cookie-confirmation-buttons-area
        #hs-eu-confirmation-button-group
        #hs-eu-opt-in-buttons {
        -webkit-text-size-adjust: 100%;
        word-break: break-word;
        pointer-events: all !important;
        --hs-banner-accentColor: #715dce;
        --hs-banner-inset: 32px auto auto 50%;
        --hs-banner-translate-x: -50%;
        --hs-banner-translate-y: 0;
        --hs-banner-close-button-display: none;
        font-weight: 400;
        text-shadow: none !important;
        -webkit-font-smoothing: antialiased;
        font-size: inherit;
        font-family: inherit;
        background: none;
        border: none;
        box-shadow: none;
        box-sizing: border-box;
        margin: 0;
        padding: 0;
        line-height: 1.75em;
        color: inherit;
        display: flex;
        justify-content: center;
        flex-flow: row wrap;
        flex-grow: 1;
        gap: 1rem !important;
    }

    #hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area button#hs-eu-confirmation-button,
    #hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area button#hs-eu-decline-button {
        text-align: center !important;
    }

    #hs-eu-policy-wording > p > a {
        text-decoration: underline !important;
        font-weight: 500 !important;
        font-family: 'Lexend Deca', 'LexendDeca', 'Helvetica Neue', helvetica, arial, sans-serif !important;
        font-size: 14.4px !important;
        color: #fff !important;
        line-height: inherit !important;
    }

    @media (max-width: 1152px) {
        #hs-banner-parent.hs-banner-parent-fixed div#hs-eu-cookie-confirmation #hs-eu-policy-wording p {
            margin-right: 2.5rem !important;
        }
    }

    @media (max-width: 800px) {
        #hs-banner-parent #hs-eu-cookie-confirmation {
            padding: 26px 8px 8px !important;

            #hs-eu-cookie-settings-button {
                line-height: 1.8rem;
                max-width: 9rem !important;
                min-width: 9rem !important;
            }
        }

        #hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner {
            padding: 0 !important;
        }

        #hs-banner-parent p {
            font-size: 0.8rem !important;
        }

        #hs-eu-cookie-confirmation-buttons-area button {
            font-size: 1rem !important;
        }
    }

    @media (max-width: 450px) {
        #hs-banner-parent div#hs-eu-cookie-confirmation div#hs-eu-cookie-confirmation-inner #hs-eu-cookie-confirmation-buttons-area button#hs-eu-cookie-settings-button {
            width: unset !important;
            text-align: center;
            height: 1.6rem;
        }
    }
</style>
